import type { SkillIconName } from '@atlassian/rovo-platform-ui-components/skills/skill-icons';
import type { Skill } from '@atlassian/rovo-platform-ui-components/skills/types';

export interface SkillDirectoryItemNode {
	readonly __typename: string;
	readonly id: string;
	readonly slashCommand?: string | null;
	readonly name?: string | null;
	readonly description?: string | null;
	readonly iconUrl?: string | null;
	readonly color?: string | null;
	readonly integrationKey?: {
		readonly displayName?: string | null;
		readonly iconUrl?: string | null;
	} | null;
	/** GraphIntegrationSkillDirectoryItem.tags — capability + registry-projected dimensions */
	readonly tags?: ReadonlyArray<{
		readonly displayName: string;
	}> | null;
}

/**
 * Structured metadata for a product integration associated with a ConvoAiSkill.
 * Mirrors the GraphQL type ConvoAiSkillIntegration.
 */
export interface ConvoAiSkillIntegration {
	/** Product key, e.g. "jira", "confluence" */
	readonly key: string;
	/** Integration identifier, e.g. "integration:jira". May be null for Anthropic skills. */
	readonly id?: string | null;
	/** Human-readable product name, e.g. "Jira". May be null if dimension metadata lookup failed. */
	readonly displayName?: string | null;
	/** Product icon URL. May be null if dimension metadata lookup failed. */
	readonly iconUrl?: string | null;
}

export interface ConvoAiSkillNode {
	readonly __typename: string;
	readonly id: string;
	readonly name?: string | null;
	readonly description?: string | null;
	readonly displayName?: string | null;
	readonly slashCommand?: string | null;
	readonly iconUrl?: string | null;
	readonly iconKey?: string | null;
	readonly color?: string | null;
	readonly source?: string | null;
	readonly tags?: ReadonlyArray<string> | null;
	readonly category?: string | null;
	/** Flat list of integration keys (e.g. ["jira", "confluence"]). Used as a fallback when `integrations` metadata is unavailable. */
	readonly integrationKeys?: ReadonlyArray<string> | null;
	/** Structured integration metadata; preferred when present. */
	readonly integrations?: ReadonlyArray<ConvoAiSkillIntegration> | null;
	readonly version?: string | null;
}

/**
 * Type guard: narrows an unknown node to a SkillDirectoryItemNode based on `__typename`.
 */
export function isSkillDirectoryItemNode(
	node: { readonly __typename?: string } | null | undefined,
): node is SkillDirectoryItemNode {
	return node?.__typename === 'GraphIntegrationSkillDirectoryItem';
}

/**
 * Type guard: narrows an unknown node to a ConvoAiSkillNode based on `__typename`.
 */
export function isConvoAiSkillNode(
	node: { readonly __typename?: string } | null | undefined,
): node is ConvoAiSkillNode {
	return node?.__typename === 'ConvoAiSkill';
}

/**
 * Parses a single GraphIntegrationSkillDirectoryItem node into a Skill.
 * Returns null if the node is not a valid skill.
 */
export function parseSkillNode(node: SkillDirectoryItemNode | null | undefined): Skill | null {
	if (!node || node.__typename !== 'GraphIntegrationSkillDirectoryItem') {
		return null;
	}
	const displayName = node.integrationKey?.displayName ?? '';
	const tagDisplayNames = node.tags?.map((t) => t.displayName) ?? [];

	const trimmedDescription =
		node.description && node.description?.length > 150
			? `${node.description?.slice(0, 147)}...`
			: node.description ?? '';

	return {
		id: node.id,
		slug: node.slashCommand?.split('/')?.[1] ?? '',
		name: node.name ?? '',
		description: trimmedDescription,
		iconName: node.iconUrl?.split('name/')?.[1] as SkillIconName,
		product: displayName ? displayName.charAt(0).toUpperCase() + displayName.slice(1) : '',
		productIconUrl: node.integrationKey?.iconUrl ?? '',
		...(node.color ? { color: node.color as Skill['color'] } : {}),
		...(tagDisplayNames.length > 0 ? { tags: [...tagDisplayNames] } : {}),
	};
}

/**
 * Parses a single ConvoAiSkill node into a Skill.
 * Returns null if the node is not a valid skill.
 */
export function parseConvoAiSkillNode(node: ConvoAiSkillNode | null | undefined): Skill | null {
	if (!node || node.__typename !== 'ConvoAiSkill') {
		return null;
	}

	const primaryIntegration = node.integrations?.[0];
	let productDisplayName = '';
	let productIconUrl = '';

	// If the skill has integrations, else use the legacy integrationKeys.
	if (primaryIntegration) {
		productDisplayName = primaryIntegration.displayName ?? '';
		productIconUrl = primaryIntegration.iconUrl ?? '';
	} else {
		const product = node.integrationKeys?.[0] ?? '';
		productDisplayName = product ? product.charAt(0).toUpperCase() + product.slice(1) : '';
	}

	return {
		id: node.id,
		slug: node.slashCommand?.split('/')?.[1] ?? '',
		name: node.displayName ?? node.name ?? '',
		description: node.description ?? '',
		iconName: (node.iconKey || node.iconUrl?.split('name/')?.[1]) as SkillIconName,
		product: productDisplayName,
		productIconUrl,
		...(node.color ? { color: node.color as Skill['color'] } : {}),
		...(node.tags && node.tags.length > 0 ? { tags: [...node.tags] } : {}),
	};
}

type SkillEdge =
	| {
			readonly node?:
				| SkillDirectoryItemNode
				| { readonly __typename: string; readonly [key: string]: unknown }
				| null
				| undefined;
	  }
	| null
	| undefined;

/**
 * Parses a GraphQL response containing skill directory item edges into Skill[].
 */
export function parseSkillEdges(edges: ReadonlyArray<SkillEdge> | null | undefined): Skill[] {
	if (!edges) {
		return [];
	}
	return edges
		.map((edge) => (isSkillDirectoryItemNode(edge?.node) ? parseSkillNode(edge.node) : null))
		.filter((skill): skill is Skill => skill !== null);
}

type ConvoAiSkillEdge =
	| {
			readonly node?:
				| ConvoAiSkillNode
				| { readonly __typename: string; readonly [key: string]: unknown }
				| null
				| undefined;
	  }
	| null
	| undefined;

/**
 * Parses a GraphQL response containing ConvoAI skill edges into Skill[].
 */
export function parseConvoAiSkillEdges(
	edges: ReadonlyArray<ConvoAiSkillEdge> | null | undefined,
): Skill[] {
	if (!edges) {
		return [];
	}
	return edges
		.map((edge) => (isConvoAiSkillNode(edge?.node) ? parseConvoAiSkillNode(edge.node) : null))
		.filter((skill): skill is Skill => skill !== null);
}

type UniversalSkillEdge =
	| {
			readonly node?:
				| SkillDirectoryItemNode
				| ConvoAiSkillNode
				| { readonly __typename: string; readonly [key: string]: unknown }
				| null
				| undefined;
	  }
	| null
	| undefined;

/**
 * Universal parser that handles both legacy and unified skill edges.
 * Automatically detects the format based on the node typename.
 */
export function parseUniversalSkillEdges(
	edges: ReadonlyArray<UniversalSkillEdge> | null | undefined,
): Skill[] {
	if (!edges) {
		return [];
	}

	return edges
		.map((edge) => {
			const node = edge?.node;
			if (!node || !node.__typename) {
				return null;
			}

			// Route to appropriate parser based on typename
			if (isSkillDirectoryItemNode(node)) {
				return parseSkillNode(node);
			}
			if (isConvoAiSkillNode(node)) {
				return parseConvoAiSkillNode(node);
			}

			return null;
		})
		.filter((skill): skill is Skill => skill !== null);
}
